import type {
  GuidedModeDefinition,
  GuidedModeStep,
  GuidedModeVisibilityRule,
} from '../types/guided-mode.types'

export const DANGER_SIGNS_GROUP_ID = 'group.danger_signs'
const DANGER_SIGNS_TITLE_TOKEN = 'group.danger_signs.prompt'
const RECENT_TEMPERATURE_CODE = 'recent_fever'

// Memoised so a re-selected track keeps the identity the focused plan and scope checks compare.
const screens = new WeakMap<GuidedModeDefinition, Map<string, GuidedModeDefinition>>()

function leafCode(definition: GuidedModeDefinition, step: GuidedModeStep): string | undefined {
  const question = step.questionId ? definition.questions[step.questionId] : undefined
  return question ? definition.concepts[question.conceptId]?.code?.code : undefined
}

function withGate(step: GuidedModeStep, gate?: GuidedModeVisibilityRule): GuidedModeStep {
  if (!gate) return step
  return { ...step, visibleWhen: step.visibleWhen ? { all: [gate, step.visibleWhen] } : gate }
}

function buildDangerSignScreen(
  definition: GuidedModeDefinition,
  codes: readonly string[],
): GuidedModeDefinition {
  const wanted = new Set(codes)
  const moved = new Map<string, GuidedModeStep>()
  const take = (step: GuidedModeStep, gate?: GuidedModeVisibilityRule): boolean => {
    const code = step.kind === 'group' ? undefined : leafCode(definition, step)
    if (!code || !wanted.has(code)) return false
    moved.set(code, withGate(step, gate))
    return true
  }
  const steps = (definition.assessment?.steps || []).flatMap(step => {
    if (step.kind !== 'group') return take(step) ? [] : [step]
    const nested = step.steps || []
    const kept = nested.filter(leaf => !take(leaf, step.visibleWhen))
    if (kept.length === nested.length) return [step]
    return kept.length ? [{ ...step, steps: kept }] : []
  })
  const anchor = steps.findIndex(step => (step.kind === 'group' ? step.steps || [] : [step])
    .some(leaf => leafCode(definition, leaf) === RECENT_TEMPERATURE_CODE))
  if (!moved.size || anchor < 0) return definition

  const screen: GuidedModeStep = {
    kind: 'group',
    id: DANGER_SIGNS_GROUP_ID,
    titleToken: DANGER_SIGNS_TITLE_TOKEN,
    multiselect: false,
    steps: codes.flatMap(code => moved.get(code) || []),
  }
  return {
    ...definition,
    assessment: {
      ...definition.assessment,
      steps: [...steps.slice(0, anchor + 1), screen, ...steps.slice(anchor + 1)],
    },
  }
}

export function withDangerSignScreen(
  definition: GuidedModeDefinition | null,
  codes: readonly string[],
): GuidedModeDefinition | null {
  if (!definition || !codes.length) return definition
  const key = codes.join('\u0000')
  const cached = screens.get(definition)?.get(key)
  if (cached) return cached
  const screen = buildDangerSignScreen(definition, codes)
  screens.set(definition, (screens.get(definition) || new Map()).set(key, screen))
  return screen
}
